import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Publisher } from '@app/core/models/product.model';
import {MessageService, MessageTypes} from '@app/core/services/message.service';
import { StaticDataCacheService } from '@app/order/services/static-data-cache.service';
import {GetMessagesResponse, Message} from '@core/models/message.model';
import {
	BehaviorSubject,
	Observable,
	merge,
	switchMap,
	expand,
	EMPTY,
	toArray,
	filter,
	take,
	defer,
	of,
	scan, last, defaultIfEmpty, tap
} from 'rxjs';
import {catchError, finalize, map, takeWhile} from "rxjs/operators";
import {BackendAPI} from "@core/models/backend-api";
import {HttpParams} from "@angular/common/http";
import {UserProfilesService} from "@core/services/user-profiles.service";
import {UserProfile} from "@core/models/user-profile";

@Component({
	selector: 'app-chat-conversations-list',
	templateUrl: './chat-conversations-list.component.html',
	styleUrls: ['./chat-conversations-list.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatConversationsListComponent implements OnInit {

	@Input()
	get showUnreadOnly() { return this._showUnreadOnly }
	set showUnreadOnly(value: any) {
		this._showUnreadOnly = (value != null && `${value}` !== 'false');
	}
	private _showUnreadOnly: boolean = false;
	private readonly MAX_PAGES = 1000;
	private readonly MESSAGE_PAGE_SIZE = 5;

	@Input()
	markMessageIdAsSelected: string | undefined;

	@Output()
	onMessageClick = new EventEmitter<{ message: Message, publisher: Publisher | undefined }>();

	constructor (
		public messageService: MessageService,
		private _staticDataCacheService: StaticDataCacheService,
		private _userProfileService: UserProfilesService
	) { }

	conversationHeads$?: Observable<Message[]>;
	readonly callingOut$ = new BehaviorSubject<boolean>(false);


	private _publishers: Publisher[] = [];
	private _conversationsReloadTrigger$ = new BehaviorSubject<void>(undefined);


	ngOnInit(): void {
		this.conversationHeads$ = merge(
			this._staticDataCacheService.publishers$.pipe(
				switchMap((publishers) => {
					this._publishers = publishers;
					return this._loadAllConversations(this._showUnreadOnly);
				}),
			),
			this._conversationsReloadTrigger$.pipe(
				switchMap(() => this._loadAllConversations(this._showUnreadOnly)),
			),
		)
	}

	public getCompanyNameById(companyId: string): string | undefined {
		return this._publishers?.find(x => x.id === companyId)?.label;
	}

	public messageClicked(message: Message) {
		this.messageService.markMessageAsRead(message.id);

		const publisherId = message.senderCompanyId || message.toCompanyId;
		let publisher = this._publishers?.find(x => x.id === publisherId);

		this.onMessageClick.emit({ message: message, publisher: publisher });
	}

	public updateConversations(): void {
		this._conversationsReloadTrigger$.next();
	}

	/**
	 * Loads conversation heads progressively, emitting messages as each page is fetched.
	 * @param unread Whether to filter for unread messages only
	 * @param pageSize Number of messages per page
	 * @param maxPages Maximum number of pages to fetch
	 * @returns Observable emitting the latest conversation heads after each page
	 */
	private _loadAllConversations(unread: boolean): Observable<Message[]> {
		let pageNo = 1;
		this.callingOut$.next(true)
		return this._userProfileService.userProfile$.pipe(
			filter((profile): profile is UserProfile => !!profile),
			take(1),
			switchMap((userProfile) =>
				this.messageService.getConversationHeads(unread, pageNo, this.MESSAGE_PAGE_SIZE, userProfile).pipe(
					expand((messages) => {
						pageNo++;
						const shouldLoadMore =
							messages.length === this.MESSAGE_PAGE_SIZE && pageNo <= this.MAX_PAGES;
						return shouldLoadMore
							? this.messageService.getConversationHeads(unread, pageNo, this.MESSAGE_PAGE_SIZE, userProfile)
							: EMPTY;
					}),
					scan(
						(acc: Message[], messages) => {
							// Combine new messages with accumulated messages
							const allMessages = [...acc, ...messages];
							// Process messages to get latest conversation heads
							return this.messageService.processMessages(allMessages, userProfile, unread);
						},
						[] as Message[],
					),
				),
			),
			catchError((error) => {
				console.error('Error in loadAllConversations:', error);
				this.callingOut$.next(false)
				return of([]);
			}),
			finalize(() => {
				this.callingOut$.next(false); // Hide loader on completion
			}),
		);
	}
}
